/**
 * useInactivityLogout hook.
 *
 * Auto-logs the user out after VITE_INACTIVITY_TIMEOUT_MINUTES of no
 * user activity (no mouse, keyboard, touch, or scroll events).
 *
 * Activated globally in App.jsx — only takes effect when the user
 * is authenticated.
 *
 * For health data, the default is 15 minutes (set in .env).
 */

import { useEffect, useRef, useCallback } from 'react';
import { useDispatch } from 'react-redux';

import { useAuth } from './useAuth';
import { logout } from '@/store/authSlice';
import { authApi } from '@/services/api';

const TIMEOUT_MS =
  (Number(import.meta.env.VITE_INACTIVITY_TIMEOUT_MINUTES) || 15) * 60 * 1000;

const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'touchstart', 'scroll', 'mousemove'];

export function useInactivityLogout() {
  const { isAuthenticated } = useAuth();
  const dispatch = useDispatch();
  const timerRef = useRef(null);

  const handleLogout = useCallback(async () => {
    try {
      await authApi.logoutRequest();
    } catch (err) {
      // Even if backend call fails, clear local state
      console.warn('Logout request failed:', err);
    }
    dispatch(logout());
  }, [dispatch]);

  const resetTimer = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(handleLogout, TIMEOUT_MS);
  }, [handleLogout]);

  useEffect(() => {
    if (!isAuthenticated) {
      if (timerRef.current) clearTimeout(timerRef.current);
      return;
    }

    // Start the timer
    resetTimer();

    // Reset on any activity (throttled by setTimeout reset)
    ACTIVITY_EVENTS.forEach((evt) => {
      window.addEventListener(evt, resetTimer, { passive: true });
    });

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      ACTIVITY_EVENTS.forEach((evt) => {
        window.removeEventListener(evt, resetTimer);
      });
    };
  }, [isAuthenticated, resetTimer]);
}
